import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mzc-pdc/ui";
import convertToByte from "../../../cores/convertToByte";
import convertUtcToLocal from "../../../cores/convertUtcToLocal";
import formatDuration from "../../../cores/formatDuration";

import compareVersions, {
    VERSION_CM_4721_SHARES,
    VERSION_CM_8782_ASSET_SHARE_LINK,
    VERSION_CM_8731_ASSET_DOWNLOAD_JOBS,
    VERSION_CM_12725_ASSET_AUTO_ARCHIVE,
    VERSION_CM_13925_OWNER_LOCK,
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_8742_ASSET_SHARE_REQUEST,
    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
} from "../../../cores/version";
import { ASSET_TYPES, GROUPING_VIEW_TYPE, LIST_VIEW_TYPE, MEDIA_TYPES } from "@constants";
import AssetTableCard from "../ui/AssetTableCard";
import { IconMoreHoriz } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import UserDetailPopper, { UserDetailButton } from "@features/user-detail-popper/user-detail-popper";
import useUserDetail from "@hooks/useUserDetail";
import { siteMode } from "@cores/siteMode";
import useCheckPermission from "@hooks/useCheckPermission";
import { getAsset } from "@modules/asset";
import AssetTableCardSkeleton from "@routes/asset/ui/asset-table-card-skeleton";
import { alpha } from "@mui/material";
import AutomaticArchivedDateLabel from "@routes/asset/ui/automatic-archived-date-label";
import { Menu } from "@components_v2/menu";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import useActionPending from "@hooks/useActionPending";
import dayjs from "dayjs";

const canCreateVideo = (asset) => {
    return (
        asset.status === "ACTIVE" &&
        asset.mediaType === MEDIA_TYPES.VIDEO &&
        asset.type !== ASSET_TYPES.DASH &&
        asset.type !== ASSET_TYPES.HLS
    );
};

const AssetTable = ({
    data,
    columns,
    draggable,
    selectedItemsDragging,
    selectedItems,
    thumbnailCrop,
    onDragStartItem,
    onClickRow,
    onDoubleClickRow,
    onDragEndItem,
    onChangeSelectItem,
    onClickToggleActionButton,
    onClickDeleteButton,
    onClickCreateJob,
    hiddenCreateJobButton,
    onClickDownload,
    onToggleModal,
    checkable = true,
    useAutoArchive,
    onClickName,
    onClickStarred,
    onClickOwnerLock,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();
    const { pending, setActionPending, disableActionPending } = useActionPending();

    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const VIDEOS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Videos" });
    const PHOTO_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Photo" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const stage = useSelector(({ stage }) => stage);
    const assets = useSelector(({ assets }) => assets);
    const stageVersion = useSelector((state) => state.stage.version);
    const propertyPanelTargetId = useSelector((state) => state.propertyPanel.targetId);
    const isCms = siteMode.get() === "CMS";

    const TableCellSkeleton = ({ width, height }) => {
        return (
            <Skeleton
                variant="rounded"
                animation={"wave"}
                width={width}
                height={height}
                sx={{ backgroundColor: alpha("#dfdfee", 0.5) }}
            />
        );
    };

    const getPopperMenu = (v) => {
        let menuList = [];

        const isOwner =
            (compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) < 0
                ? v.createdBy?.id
                : v.owner?.id) === stage.user?.id;
        const disabledByOwnerLock = v.isOwnerLocked && !isOwner;

        const isExpired = v?.shareInfo?.expiresAt
            ? countDaysBetweenDates({ toDate: dayjs(v?.shareInfo?.expiresAt) }) < 0
            : false;
        const isExceedDownloadCount =
            v?.shareInfo?.maxDownloads > 0 && v?.shareInfo?.maxDownloads <= v?.shareInfo?.downloadCount;
        const hasDownloadPermissionByShare = v?.shareInfo && !(isExpired || isExceedDownloadCount);
        const hiddenDownloadButton =
            (!v?.shareInfo || !hasDownloadPermissionByShare) && (!DOWNLOAD_PERMISSIONS.DOWNLOAD || disabledByOwnerLock);

        menuList = [
            {
                label: t(`common::label::Share link`, `Share link`),
                onClick: () => {
                    onToggleModal("shareLink", true, v);
                },
                hidden: !(!isCms && compareVersions(stage.version, VERSION_CM_8782_ASSET_SHARE_LINK) >= 0),
                disabled: v.state === "DRAFT" || !SHARES_PERMISSIONS.EXTERNAL || disabledByOwnerLock,
                clickClose: true,
            },
            {
                label: t(`common::label::Share request`, `Share request`),
                onClick: () => {
                    onToggleModal("requestShare", true, v);
                },
                hidden: isCms || compareVersions(stageVersion, VERSION_CM_13766_SHARES_V3) >= 0,
                disabled: v.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                clickClose: true,
            },
            {
                label: t(`common::label::Copy to`, `Copy to`),
                divider: true,
                onClick: () => {
                    onToggleModal("copyCategory", true, v);
                },
                disabled: v.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                hidden: !(
                    !isCms &&
                    assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                    assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY
                ),
                clickClose: true,
            },
            {
                label: t(`common::label::Move`, `Move`),
                divider: !(
                    assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                    assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY
                ),
                disabled: v.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                onClick: () => {
                    if (assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                        onToggleModal("moveCategory", true, v);
                    } else {
                        onToggleModal("moveFolder", true, v);
                    }
                    //category, folder에 따라 분기처리가 필요
                },
                hidden: !(!isCms && assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY),
                clickClose: true,
            },
            {
                label: t(`common::label::Rename`, `Rename`),
                divider: assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.LIST,
                onClick: () => {
                    onToggleModal("rename", true, v);
                },
                disabled: v.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                hidden: isCms,
                clickClose: true,
            },
            {
                label: t(`common::label::${"Download"}`, "Download"),
                divider: !isCms,
                onClick: () => {
                    onClickDownload(v);
                },
                disabled:
                    v.state === "DRAFT" ||
                    (compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 && !DOWNLOAD_PERMISSIONS.DOWNLOAD),
                hidden:
                    compareVersions(stage.version, VERSION_CM_8731_ASSET_DOWNLOAD_JOBS) < 0 ||
                    (compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 && hiddenDownloadButton),
                clickClose: true,
            },
            {
                label: t(`common::label::Request download`, `Request download`),
                onClick: () => {
                    onToggleModal("requestDownload", true, v);
                },
                disabled: v.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 || !hiddenDownloadButton,
                clickClose: true,
            },
            {
                label: t(`common::label::Request`, `Request`),
                onClick: () => {
                    onToggleModal("request", true, v);
                },
                disabled: v.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0,
                clickClose: true,
            },
            {
                label: t(
                    `common::label::${v?.myFavorite ? "Unstarred" : "Starred"}`,
                    v?.myFavorite ? "Unstarred" : "Starred",
                ),
                onClick: () => {
                    onClickStarred([v.id], v?.myFavorite);
                },
                disabled: v.state === "DRAFT",
                clickClose: true,
            },
            {
                label: v.isOwnerLocked ? t(`common::label::Unlock`, `Unlock`) : t(`common::label::Lock`, `Lock`),
                divider: false,
                onClick: (e) => {
                    onClickOwnerLock(v);
                },
                disabled: v.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || !isOwner,
                hidden: !(!isCms && compareVersions(stage.version, VERSION_CM_13925_OWNER_LOCK) >= 0),
                clickClose: true,
            },
            {
                label: t(`common::label::${"Delete"}`, "Delete"),
                divider: true,
                onClick: () => {
                    onClickDeleteButton(v);
                },
                hidden: isCms,
                disabled: !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                clickClose: true,
            },
        ];

        return (
            <Menu
                menuMinWidth={160}
                placement={"right"}
                type={"icon"}
                label={<IconMoreHoriz size={16} />}
                size={"small"}
                list={menuList}
            />
        );
    };

    return (
        <>
            <TableContainer sx={{ borderTop: `1px solid ${theme.palette.divider}`, overflow: "auto", height: `100%` }}>
                <Table stickyHeader shadowHeader borderHeader>
                    <colgroup>
                        <col style={{ width: `860px`, minWidth: "520px", maxWidth: "860px" }} />
                        <col style={{ width: `120px`, minWidth: `120px` }} />
                        {useAutoArchive && <col style={{ width: `210px`, minWidth: `210px` }} />}
                        <col style={{ width: `175px`, minWidth: `175px` }} />
                        <col style={{ width: `100px`, minWidth: `100px` }} />
                        <col style={{ width: `32px`, minWidth: `32px` }} />
                    </colgroup>
                    <TableHead>
                        <TableRow sx={{ th: { whiteSpace: `nowrap` } }}>
                            {columns.map((column, index) => (
                                <TableCell
                                    key={column.id || index}
                                    sx={{ pl: theme.spacing(column.id === "name" ? 6.5 : 2.5) }}
                                >
                                    {t(`common::label::${column.label}`)}
                                </TableCell>
                            ))}
                            <TableCell align={"center"}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.length > 0 ? (
                            <React.Fragment>
                                {data?.map((v, i) => {
                                    if (v.state === "DRAFT")
                                        return (
                                            <TableRow
                                                key={i}
                                                draggable={draggable}
                                                hover
                                                selected={v.id === propertyPanelTargetId}
                                                onDragStart={(e) => onDragStartItem(e, v)}
                                                onDragEnd={(e) => onDragEndItem(e, v)}
                                                onClick={() => {
                                                    onClickRow(v.id, v.project.id);
                                                }}
                                                onDoubleClick={() => {
                                                    onDoubleClickRow(v);
                                                }}
                                                className={clsx({
                                                    "is-active": v.id === propertyPanelTargetId,
                                                    "is-dragging": selectedItems.find((item) => item.id === v.id)
                                                        ? true
                                                        : false && selectedItemsDragging,
                                                })}
                                            >
                                                <TableCell>
                                                    <AssetTableCardSkeleton
                                                        asset={v}
                                                        stageVersion={stageVersion}
                                                        onChangeSelectItem={onChangeSelectItem}
                                                        selectedItems={selectedItems}
                                                        checkable={checkable}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TableCellSkeleton height={22} />
                                                </TableCell>
                                                {compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >=
                                                    0 &&
                                                    useAutoArchive && (
                                                        <TableCell>
                                                            {v.hasOwnProperty("autoArchivedAt") ? (
                                                                <AutomaticArchivedDateLabel date={v.autoArchivedAt} />
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </TableCell>
                                                    )}
                                                <TableCell>
                                                    <time>
                                                        {v.createdAt
                                                            ? convertUtcToLocal(v.createdAt, "YYYY-MM-DD HH:mm")
                                                            : "-"}
                                                    </time>
                                                </TableCell>
                                                <TableCell>
                                                    {compareVersions(
                                                        stage.version,
                                                        VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
                                                    ) < 0 ? (
                                                        v.createdBy && v.createdBy.username ? (
                                                            <UserDetailButton
                                                                maxWidth={120}
                                                                onClick={(e) =>
                                                                    handleUserProfileClick(e, v.createdBy?.id)
                                                                }
                                                                userName={v.createdBy.name}
                                                            />
                                                        ) : (
                                                            "-"
                                                        )
                                                    ) : v.owner && v.owner.name ? (
                                                        <UserDetailButton
                                                            maxWidth={120}
                                                            onClick={(e) => handleUserProfileClick(e, v.owner?.id)}
                                                            userName={v.owner.name}
                                                        />
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    onDoubleClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    {getPopperMenu(v)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    return (
                                        <TableRow
                                            key={i}
                                            draggable={draggable}
                                            hover
                                            checked={selectedItems.find((item) => item.id === v.id)}
                                            selected={v.id === propertyPanelTargetId}
                                            onDragStart={(e) => onDragStartItem(e, v)}
                                            onDragEnd={(e) => onDragEndItem(e, v)}
                                            onClick={() => {
                                                if (ASSETS_PERMISSIONS.READ) onClickRow(v.id, v.project.id);
                                            }}
                                            onDoubleClick={() => {
                                                onDoubleClickRow(v);
                                            }}
                                            className={clsx({
                                                "is-active": v.id === propertyPanelTargetId,
                                                "is-checked": selectedItems.find((item) => item.id === v.id)
                                                    ? true
                                                    : false,
                                                "is-dragging": selectedItems.find((item) => item.id === v.id)
                                                    ? true
                                                    : false && selectedItemsDragging,
                                            })}
                                        >
                                            <TableCell>
                                                <AssetTableCard
                                                    asset={v}
                                                    stageVersion={stageVersion}
                                                    onChangeSelectItem={onChangeSelectItem}
                                                    selectedItems={selectedItems}
                                                    checkable={checkable}
                                                    thumbnailCrop={thumbnailCrop}
                                                    onClickName={onClickName}
                                                />
                                            </TableCell>
                                            <TableCell>{v.size ? convertToByte(v.size) : "-"}</TableCell>
                                            {compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0 &&
                                                useAutoArchive && (
                                                    <TableCell>
                                                        {v.hasOwnProperty("autoArchivedAt") ? (
                                                            <AutomaticArchivedDateLabel date={v.autoArchivedAt} />
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </TableCell>
                                                )}
                                            <TableCell>
                                                <time>
                                                    {v.createdAt
                                                        ? convertUtcToLocal(v.createdAt, "YYYY-MM-DD HH:mm")
                                                        : "-"}
                                                </time>
                                            </TableCell>
                                            <TableCell>
                                                {compareVersions(
                                                    stage.version,
                                                    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
                                                ) < 0 ? (
                                                    v.createdBy && v.createdBy.username ? (
                                                        <UserDetailButton
                                                            maxWidth={120}
                                                            onClick={(e) => handleUserProfileClick(e, v.createdBy?.id)}
                                                            userName={v.createdBy.name}
                                                        />
                                                    ) : (
                                                        "-"
                                                    )
                                                ) : v.owner && v.owner.name ? (
                                                    <UserDetailButton
                                                        maxWidth={120}
                                                        onClick={(e) => handleUserProfileClick(e, v.owner?.id)}
                                                        userName={v.owner.name}
                                                    />
                                                ) : (
                                                    "-"
                                                )}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                onDoubleClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            >
                                                {getPopperMenu(v)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <TableRow>
                                    <TableCell colSpan={9} className={"text-center"}>
                                        {t(`common::label::Empty {{name}}`, { name: t(`common::label::Assets`) })}
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {!userProfilePending && (
                <UserDetailPopper
                    id={openUserProfile ? "user-profile-popover" : undefined}
                    handleUserProfileClose={handleUserProfileClose}
                    userProfileAnchorEl={userProfileAnchorEl}
                    openUserProfile={openUserProfile}
                    userProfileData={userProfileData}
                />
            )}
        </>
    );
};

export default React.memo(AssetTable);
